import { plural } from '@srm/ui/lib/pluralize';
import type { IDocumentStatusBar, IStatusBarBranch, IStatusBarTask } from '@models/document/IDocumentStatusBar';
import { capitalize } from '@/utils/capitalize';
import { formatDayMonth } from '@/utils/date';
import {
    blockerOf,
    branchLabel,
    days,
    findFront,
    formatDue,
    frontElapsed,
    hours,
    isFinished,
    lowerFirst,
    stageNumber,
    type TenderDue,
    type TenderTone,
} from '@/utils/tenderStatus';

// Модель вкладки «Статус тендера»: заголовок «сейчас», держатели, строки блокировки и ход закупки по уровням.

type StepState = 'done' | 'now' | 'next';

interface TenderHeadline {
    title: string;
    sub?: string;
    finished?: boolean;
}

interface TenderStep {
    title: string;
    state: StepState;
    tone?: TenderTone;
    /** Ветки на подэтапе: «ПТК №627» */
    note?: string;
}

export interface TenderLevel {
    title: string;
    state: StepState;
    tone?: TenderTone;
    /** «20 октября, за минуту», «с 20 октября», «3 этапа» */
    meta: string;
    steps: TenderStep[];
}

interface HolderBase {
    key: string;
    branch: IStatusBarBranch;
}

export type TenderHolder =
    | (HolderBase & {
          kind: 'task';
          task: IStatusBarTask;
          due: TenderDue;
          /** Следующая задача той же ветки: документ уже назван строкой выше */
          continues: boolean;
          /** «на доработке с 22 октября» — у ветки, которая не открытый документ */
          docState?: string;
      })
    | (HolderBase & { kind: 'doc'; docState?: string });

interface TenderNote {
    key: string;
    tone: TenderTone;
    title: string;
    text?: string;
    /** Документ ветки (не открытый) — ссылка на его маршруты */
    docId?: string;
}

const steps = (n: number): string => `${n} ${plural(n, ['этап', 'этапа', 'этапов'])}`;
const samePos = (a: { level: number; item: number }, b: { level: number; item: number }) =>
    a.level === b.level && a.item === b.item;
const worst = (tones: (TenderTone | undefined)[]): TenderTone | undefined =>
    tones.includes('bad') ? 'bad' : tones.includes('wait') ? 'wait' : undefined;
const withoutDot = (text: string): string => text.trim().replace(/[.\s]+$/, '');
const DAY = 86_400_000;

/** @internal Экспорт — для тестов. Длительность пройденного: «за минуту», «за 5 минут», «за 3 часа», «за 2 дня». */
export function formatSpan(fromIso: string, toIso: string): string {
    const ms = Math.max(0, Date.parse(toIso) - Date.parse(fromIso));
    const minutes = Math.round(ms / 60_000);
    if (minutes < 2) return 'за минуту';
    if (minutes < 60) return `за ${minutes} ${plural(minutes, ['минуту', 'минуты', 'минут'])}`;
    if (ms < DAY) return `за ${hours(Math.round(minutes / 60))}`;
    return `за ${days(Math.max(1, Math.round(ms / DAY)))}`;
}

/** Ядро пишет «Обработка исключения '<причина>' при выполнении '<задача>'» — показываем задачу и причину. */
const ROUTE_ERROR = /^Обработка исключения\s+'(.+?)'\s+при\s+(?:выполнении|создании)\s+'(.+)'$/;
const routeErrorText = (text: string): string => {
    const match = ROUTE_ERROR.exec(text.trim());
    return match ? `Задача «${match[2]}»: ${match[1]}` : withoutDot(text);
};

/** Позиции веток-держателей без повторов. */
function holderPositions(holders: readonly IStatusBarBranch[]): { level: number; item: number }[] {
    return holders.filter((b, i) => holders.findIndex((o) => samePos(o, b)) === i);
}

/**
 * Заголовок левой колонки и подстрока «Уровень, этап N из M, идёт K-й день». Нет фронта у незавершённой закупки —
 * «Этап не определён».
 */
export function buildHeadline(
    bar: IDocumentStatusBar,
    holders: readonly IStatusBarBranch[],
    now: number,
): TenderHeadline | undefined {
    const levels = bar.levels ?? [];
    if (!levels.length) return undefined;
    if (isFinished(bar)) {
        const of = levels.reduce((sum, level) => sum + level.childs.length, 0);
        return {
            title: 'Закупка завершена',
            sub: of > 1 ? `Все ${steps(of)} пройдены` : 'Этап пройден',
            finished: true,
        };
    }
    const front = findFront(levels);
    if (!front) return { title: 'Этап не определён' };
    const { n, of } = stageNumber(levels, front);
    if (holderPositions(holders).length > 1) {
        return { title: 'Ветки идут параллельно', sub: `Дальше всех: ${front.stage.title}, этап ${n} из ${of}` };
    }
    const elapsed = frontElapsed(bar, now);
    const parts = [front.levelTitle, `этап ${n} из ${of}`];
    if (elapsed) parts.push(`идёт ${elapsed}`);
    return { title: front.stage.title, sub: parts.join(', ') };
}

/** Строка ветки под документом: «на доработке с 22 октября». */
function docState(branch: IStatusBarBranch, now: number): string | undefined {
    if (!branch.docStatus) return undefined;
    const status = lowerFirst(branch.docStatus);
    return branch.enteredAt ? `${status} с ${formatDayMonth(branch.enteredAt, now)}` : status;
}

/** Кто держит закупку: строка на задачу; ветка без задач — строка документа. */
export function buildHolders(holders: readonly IStatusBarBranch[], now: number): TenderHolder[] {
    return holders.flatMap<TenderHolder>((branch) => {
        const state = branch.isCurrent ? undefined : docState(branch, now);
        if (!branch.tasks?.length) {
            return branch.isCurrent ? [] : [{ kind: 'doc', key: `doc:${branch.docId}`, branch, docState: state }];
        }
        return branch.tasks.map((task, ti) => ({
            kind: 'task' as const,
            key: `task:${branch.docId}:${ti}`,
            branch,
            task,
            due: formatDue(task, now),
            continues: ti > 0,
            docState: state,
        }));
    });
}

/** Строки блокировки: ошибка маршрута — красная, ожидание — оранжевая. Просрочка видна в сроке задачи. */
export function buildNotes(holders: readonly IStatusBarBranch[], now: number): TenderNote[] {
    const notes: TenderNote[] = [];
    holders.forEach((branch, bi) => {
        const prefix = branch.isCurrent ? '' : `${branchLabel(branch)}: `;
        const docId = branch.isCurrent ? undefined : branch.docId;
        const errors = branch.routeErrors ?? [];
        errors.forEach((error, ei) => {
            if (errors.findIndex((e) => e.text === error.text) !== ei) return;
            const since = error.since ? `, с ${formatDayMonth(error.since, now)}` : '';
            notes.push({
                key: `error:${bi}:${ei}`,
                tone: 'bad',
                title: 'Маршрут остановлен.',
                text: `${prefix}${routeErrorText(error.text)}${since}.`,
                docId,
            });
        });
        if (branch.hold) {
            const status = !branch.isCurrent && branch.docStatus ? ` в статусе «${branch.docStatus}»` : '';
            notes.push({
                key: `hold:${bi}`,
                tone: 'wait',
                title: `${capitalize(withoutDot(branch.hold))}.`,
                text: branch.isCurrent ? undefined : `${branchLabel(branch)}${status}.`,
                docId,
            });
        }
    });
    return notes;
}

/** Даты уровня: первый вход в его подэтапы (у первого бывает только выход) и последний выход. */
function levelDates(bar: IDocumentStatusBar, level: number): { from?: string; to?: string } {
    const childs = bar.levels[level].childs;
    const entered = childs.map((c) => c.enteredAt ?? c.leftAt).filter((d): d is string => !!d);
    const left = childs.map((c) => c.leftAt).filter((d): d is string => !!d);
    const pick = (list: string[], sign: 1 | -1) =>
        list.reduce<string | undefined>(
            (best, d) => (!best || sign * (Date.parse(d) - Date.parse(best)) > 0 ? d : best),
            undefined,
        );
    return { from: pick(entered, -1), to: pick(left, 1) };
}

/** Ход закупки по уровням: пройденный свёрнут в «20 октября, за минуту», текущий — с ветками, будущий — «3 этапа». */
export function buildRoute(bar: IDocumentStatusBar, holders: readonly IStatusBarBranch[], now: number): TenderLevel[] {
    const levels = bar.levels ?? [];
    const front = findFront(levels);
    return levels.map((level, li) => {
        const routeSteps = level.childs.map<TenderStep>((child, ci) => {
            const here = holders.filter((b) => b.level === li && b.item === ci);
            const isFront = !!front && front.level === li && front.item === ci;
            const labels = here.filter((b) => !b.isCurrent).map(branchLabel);
            const state: StepState = isFront || here.length ? 'now' : child.status === 'Done' ? 'done' : 'next';
            return {
                title: child.title,
                state,
                tone: state === 'now' ? blockerOf(here)?.tone : undefined,
                note: labels.length ? [...new Set(labels)].join(', ') : undefined,
            };
        });
        const isNow = routeSteps.some((s) => s.state === 'now');
        const isDone =
            !isNow &&
            (level.status === 'Done' || (level.childs.length > 0 && routeSteps.every((s) => s.state === 'done')));
        const { from, to } = levelDates(bar, li);
        let meta: string;
        if (isDone)
            meta = from && to ? `${formatDayMonth(from, now)}, ${formatSpan(from, to)}` : steps(level.childs.length);
        else if (isNow) meta = from ? `с ${formatDayMonth(from, now)}` : 'сейчас';
        else meta = steps(level.childs.length);
        return {
            title: level.title,
            state: isNow ? 'now' : isDone ? 'done' : 'next',
            tone: worst(routeSteps.map((s) => s.tone)),
            meta,
            steps: routeSteps,
        };
    });
}
